import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  User as UserIcon,
} from 'lucide-react';
import { authApi } from '@/features/auth/api';
import { authStore } from '@/features/auth/store';
import { useAuth } from '@/features/auth/useAuth';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { ThemeToggle } from '@/features/theme/ThemeToggle';
import { LanguageToggle } from '@/features/i18n/LanguageToggle';
import { SidebarNav } from './SidebarNav';

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage?.getItem('kreart.sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // Restore focus to the hamburger when the drawer closes (Radix Dialog also
  // does this by default, but we keep an explicit ref for safety in tests).
  useEffect(() => {
    if (!drawerOpen) hamburgerRef.current?.focus({ preventScroll: true });
  }, [drawerOpen]);

  useEffect(() => {
    try {
      window.localStorage?.setItem('kreart.sidebarCollapsed', String(sidebarCollapsed));
    } catch {
      // Some test/browser privacy contexts expose no localStorage.
    }
  }, [sidebarCollapsed]);

  const onLogout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      /* backend clears cookie; even on error we drop local state */
    }
    authStore.setUnauthenticated();
    queryClient.clear();
    await navigate({ to: '/login', search: {} });
  };

  return (
    <div className="min-h-full flex">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow"
      >
        {t('common.skipToMain')}
      </a>

      {/* Desktop sidebar — visible on lg and up. */}
      <aside
        className={`hidden shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 lg:flex ${
          sidebarCollapsed ? 'w-16' : 'w-60'
        }`}
      >
        <div
          className={`flex items-center gap-2 px-3 py-5 ${
            sidebarCollapsed ? 'justify-center' : 'justify-between'
          }`}
        >
          <Link
            to="/"
            className={`font-bold tracking-tight text-primary ${
              sidebarCollapsed ? 'text-xl' : 'text-2xl'
            }`}
            aria-label="KREART"
          >
            {sidebarCollapsed ? 'K' : 'KREART'}
          </Link>
          <Button
            type="button"
            variant="ghost"
            className="h-8 w-8 shrink-0 p-0"
            onClick={() => setSidebarCollapsed((value) => !value)}
            aria-label={t(sidebarCollapsed ? 'nav.expandSidebar' : 'nav.collapseSidebar')}
            title={t(sidebarCollapsed ? 'nav.expandSidebar' : 'nav.collapseSidebar')}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="h-4 w-4" aria-hidden="true" focusable="false" />
            ) : (
              <PanelLeftClose className="h-4 w-4" aria-hidden="true" focusable="false" />
            )}
          </Button>
        </div>
        <SidebarNav pathname={pathname} collapsed={sidebarCollapsed} />
      </aside>

      {/* Mobile drawer — Radix Sheet, opens via hamburger. */}
      <Sheet
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) hamburgerRef.current?.focus({ preventScroll: true });
          setDrawerOpen(open);
        }}
      >
        <SheetContent
          side="left"
          aria-label={t('nav.primary', { defaultValue: 'Main navigation' })}
        >
          <SheetTitle className="sr-only">
            {t('nav.primary', { defaultValue: 'Main navigation' })}
          </SheetTitle>
          <div className="px-4 py-5">
            <Link
              to="/"
              onClick={() => setDrawerOpen(false)}
              className="text-2xl font-bold tracking-tight text-primary"
            >
              KREART
            </Link>
          </div>
          <SidebarNav pathname={pathname} onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 border-b border-border bg-card">
          <div className="flex items-center gap-2 px-4 py-3 sm:gap-4 sm:px-6">
            <Button
              ref={hamburgerRef}
              variant="ghost"
              className="lg:hidden h-9 w-9 p-0"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('nav.openMenu')}
              aria-expanded={drawerOpen}
              aria-controls="mobile-nav-drawer"
              data-testid="mobile-nav-toggle"
            >
              <Menu className="h-5 w-5" aria-hidden="true" focusable="false" />
            </Button>

            <Link
              to="/"
              className="text-lg font-bold tracking-tight text-primary lg:hidden"
              aria-hidden="true"
              tabIndex={-1}
            >
              KREART
            </Link>

            <div className="relative hidden flex-1 max-w-xl sm:block">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
                focusable="false"
              />
              <input
                type="text"
                placeholder={t('nav.search')}
                className="w-full h-9 rounded-md border border-border bg-input-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label={t('nav.search')}
              />
            </div>
            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <LanguageToggle />
              <ThemeToggle />
              {user && (
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10"
                    aria-hidden="true"
                  >
                    <UserIcon className="h-4 w-4 text-primary" focusable="false" />
                  </div>
                  <span className="text-sm font-medium hidden md:inline">{user.fullName}</span>
                </div>
              )}
              <Button
                variant="ghost"
                onClick={onLogout}
                className="gap-2"
                data-testid="logout-button"
                aria-label={t('nav.logout')}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" focusable="false" />
                <span className="hidden md:inline">{t('nav.logout')}</span>
              </Button>
            </div>
          </div>
        </header>
        <main id="main" className="flex-1 bg-muted/30">
          <div className="w-full px-4 py-6 sm:px-6 sm:py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
